import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import useEventStore, {
    markPopupPermanentlyDisabled,
    markPopupSnoozed,
    markSessionPopupShown,
    resolvePopupCandidate,
    type PopupCandidate,
} from '../../store/eventStore';
import EventLiveCountdownBadge from '../EventLiveCountdownBadge';

interface EventPromotionModalProps {
    enabled?: boolean;
    navigateTo?: (pageKey: string) => void;
}

const EventPromotionModal: React.FC<EventPromotionModalProps> = ({
    enabled = true,
    navigateTo,
}) => {
    const fetchEvents = useEventStore((s) => s.fetchEvents);
    const fetchPromotionSeminars = useEventStore((s) => s.fetchPromotionSeminars);

    const [candidate, setCandidate] = useState<PopupCandidate | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [confirmOptOutOpen, setConfirmOptOutOpen] = useState(false);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;

        const run = async () => {
            await Promise.all([fetchEvents(), fetchPromotionSeminars()]);
            if (cancelled) return;
            const { events: live, promotionSeminars: promos } = useEventStore.getState();
            const next = resolvePopupCandidate(live, promos);
            if (!next || cancelled) return;
            markSessionPopupShown();
            setCandidate(next);
            setIsOpen(true);
        };

        const timer = window.setTimeout(() => {
            run().catch((err) => console.error('[EventPromotionModal]', err));
        }, 1200);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
        // Nur einmal pro Mount/Enable-Zyklus evaluieren (Session-Drossel)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    const closeSoft = useCallback(() => {
        if (candidate) markPopupSnoozed(candidate.id);
        setConfirmOptOutOpen(false);
        setIsOpen(false);
        setCandidate(null);
    }, [candidate]);

    const confirmPermanentOptOut = useCallback(() => {
        if (candidate) markPopupPermanentlyDisabled(candidate.id);
        setConfirmOptOutOpen(false);
        setIsOpen(false);
        setCandidate(null);
    }, [candidate]);

    const handleCta = useCallback(() => {
        if (!candidate) return;
        markPopupPermanentlyDisabled(candidate.id);
        setIsOpen(false);

        try {
            if (candidate.kind === 'reminder') {
                sessionStorage.setItem('peone_focus_event_id', candidate.id);
            }
        } catch {
            /* ignore */
        }

        if (candidate.ctaIsExternal && candidate.ctaHref) {
            window.open(candidate.ctaHref, '_blank', 'noopener,noreferrer');
        } else if (candidate.navigatePageKey && navigateTo) {
            navigateTo(candidate.navigatePageKey);
        } else if (candidate.ctaHref) {
            window.location.assign(candidate.ctaHref);
        }

        setCandidate(null);
    }, [candidate, navigateTo]);

    const badgeLabel =
        candidate?.kind === 'reminder' ? 'Live-Event startet bald' : 'Seminar-Empfehlung';
    const optOutLabel =
        candidate?.kind === 'reminder'
            ? 'Dieses Event nicht mehr anzeigen'
            : 'Dieses Seminar nicht mehr anzeigen';
    const optOutConfirmText =
        candidate?.kind === 'reminder'
            ? 'Möchtest du Erinnerungen zu diesem Event wirklich dauerhaft deaktivieren?'
            : 'Möchtest du Erinnerungen zu diesem Seminar wirklich dauerhaft deaktivieren?';

    return (
        <AnimatePresence>
            {isOpen && candidate && (
                <motion.div
                    key="event-promotion-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[95000] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 font-sans"
                >
                    <motion.div
                        initial={{ scale: 0.96, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.96, opacity: 0 }}
                        className="relative w-full max-w-lg bg-[#15171e] border border-[#E6C299]/35 rounded-2xl shadow-[0_0_50px_rgba(230,194,153,0.15)] overflow-hidden"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="event-promo-title"
                    >
                        <button
                            type="button"
                            onClick={closeSoft}
                            className="absolute top-3 right-3 z-10 p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                            aria-label="Schließen"
                            title="Später erinnern"
                        >
                            <X size={18} />
                        </button>

                        {candidate.imageUrl ? (
                            <div className="relative w-full aspect-[16/9] bg-black/40 overflow-hidden border-b border-[#E6C299]/20">
                                <img
                                    src={candidate.imageUrl}
                                    alt=""
                                    className="w-full h-full object-cover"
                                />
                                {candidate.kind === 'reminder' && candidate.startsAtMs != null && (
                                    <div className="absolute bottom-3 left-3 right-3 flex justify-center pointer-events-none">
                                        <EventLiveCountdownBadge startsAtMs={candidate.startsAtMs} />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="h-2 w-full bg-gradient-to-r from-[#E6C299]/80 via-[#E6C299]/40 to-transparent" />
                        )}

                        <div className="p-6 sm:p-8">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#E6C299]">
                                    {badgeLabel}
                                </p>
                                {!candidate.imageUrl && candidate.kind === 'reminder' && candidate.startsAtMs != null && (
                                    <EventLiveCountdownBadge startsAtMs={candidate.startsAtMs} />
                                )}
                            </div>
                            <h2
                                id="event-promo-title"
                                className="text-xl sm:text-2xl font-black text-white uppercase tracking-wide leading-tight mb-2 pr-8"
                            >
                                {candidate.title}
                            </h2>
                            {candidate.subtitle && (
                                <p className="text-sm text-slate-400 italic mb-3">{candidate.subtitle}</p>
                            )}
                            {(candidate.dateLabel || candidate.timeLabel) && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {candidate.dateLabel && (
                                        <span className="inline-flex items-center gap-1.5 bg-black/40 border border-slate-700 px-3 py-1.5 rounded-lg text-xs font-mono text-slate-200">
                                            {candidate.dateLabel}
                                        </span>
                                    )}
                                    {candidate.timeLabel && (
                                        <span className="inline-flex items-center gap-1.5 bg-black/40 border border-slate-700 px-3 py-1.5 rounded-lg text-xs font-mono text-slate-200">
                                            {candidate.timeLabel}
                                        </span>
                                    )}
                                </div>
                            )}
                            {candidate.description && (
                                <p className="text-sm text-slate-300 leading-relaxed mb-6 line-clamp-4 whitespace-pre-line">
                                    {candidate.description}
                                </p>
                            )}

                            <div className="flex flex-col gap-3">
                                <button
                                    type="button"
                                    onClick={handleCta}
                                    className="w-full py-3.5 rounded-xl bg-[#E6C299] text-black font-black uppercase tracking-widest text-xs hover:bg-[#f0d4b0] transition-colors shadow-lg shadow-[#E6C299]/20"
                                >
                                    {candidate.ctaLabel}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeSoft}
                                    className="w-full py-3 rounded-xl border border-slate-700 text-slate-300 font-bold uppercase tracking-widest text-[10px] hover:bg-slate-800/60 transition-colors"
                                >
                                    Später erinnern
                                </button>
                            </div>

                            <div className="mt-6 pt-4 border-t border-slate-800 text-center">
                                <button
                                    type="button"
                                    onClick={() => setConfirmOptOutOpen(true)}
                                    className="text-[11px] text-slate-500 hover:text-slate-300 underline underline-offset-2 transition-colors"
                                >
                                    {optOutLabel}
                                </button>
                            </div>
                        </div>

                        <AnimatePresence>
                            {confirmOptOutOpen && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 z-20 bg-black/90 flex items-center justify-center p-6"
                                >
                                    <div className="bg-[#111] border border-[#E6C299]/30 rounded-xl p-6 max-w-sm w-full text-center">
                                        <p className="text-sm text-slate-300 leading-relaxed mb-6">
                                            {optOutConfirmText}
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setConfirmOptOutOpen(false)}
                                                className="flex-1 py-3 rounded-xl border border-slate-600 text-slate-300 text-[10px] font-bold uppercase tracking-widest hover:bg-slate-800"
                                            >
                                                Abbrechen
                                            </button>
                                            <button
                                                type="button"
                                                onClick={confirmPermanentOptOut}
                                                className="flex-1 py-3 rounded-xl bg-red-600/90 hover:bg-red-500 text-white text-[10px] font-black uppercase tracking-widest"
                                            >
                                                Ja, deaktivieren
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default EventPromotionModal;
